import type { SxProps, Theme } from '@mui/material/styles';
import BasicSnackBar from './BasicSnackBar';
import { useProgramStateNotification, type ProgramNotificationType } from '../../hooks/ui/useProgramStateNotification';
import { constrainedBottomSnackbarContentSx } from './snackbarStyles';

const INVALID_FILENAME_ERROR_AUTO_HIDE_DURATION_MS = 10000;

export const getProgramStateSnackbarAutoHideDuration = (type: ProgramNotificationType): number | undefined =>
  type === 'failed' ? INVALID_FILENAME_ERROR_AUTO_HIDE_DURATION_MS : undefined;

export const getProgramStateSnackbarContentSx = (type: ProgramNotificationType): SxProps<Theme> | undefined => {
  if (type === 'failed') {
    return (theme) => ({
      backgroundColor: theme.vars?.palette?.error?.main ?? theme.palette.error.main,
      color: theme.vars?.palette?.error?.contrastText ?? theme.palette.error.contrastText,
      ...constrainedBottomSnackbarContentSx,
    });
  }

  if (type === 'deploying' || type === 'deployed') {
    return (theme) => ({
      backgroundColor: theme.vars?.palette?.chart?.blue ?? theme.palette.chart.blue,
      color: theme.vars?.palette?.common?.black ?? theme.palette.common.black,
      ...constrainedBottomSnackbarContentSx,
    });
  }

  if (type === 'running' || type === 'stopped') {
    return (theme) => ({
      backgroundColor: theme.vars?.palette?.chart?.green ?? theme.palette.chart.green,
      color: theme.vars?.palette?.common?.black ?? theme.palette.common.black,
      ...constrainedBottomSnackbarContentSx,
    });
  }

  return undefined;
};

/**
 * Renders the program deploy/run/stop status snackbar at the app root.
 * Placed at App level so it persists across all pages/tabs.
 */
const ProgramStateSnackbar = () => {
  const programNotification = useProgramStateNotification();

  return (
    <BasicSnackBar
      message={programNotification.message}
      vertical="bottom"
      horizontal="left"
      open={programNotification.open}
      onClose={programNotification.onClose}
      onExited={programNotification.onExited}
      persistent={programNotification.persistent}
      autoHideDuration={getProgramStateSnackbarAutoHideDuration(programNotification.type)}
      contentSx={getProgramStateSnackbarContentSx(programNotification.type)}
    />
  );
};

export default ProgramStateSnackbar;
